import { FC, PropsWithChildren, RefObject, memo, useContext, useEffect, useRef } from 'react';
import { ZoomContext, ZoomableDataProvider } from './Providers/d3Zoomable';
import {
   useClusteredDataContext,
   Cluster as ClusterType,
   ClusteredDataProvider,
} from './Providers/clusteredData';
import {
   DataContextProvider,
   RequiredDataContextProps,
   HotSpotItem,
} from './Providers/dataContext';
import { ScaledDataProvider } from './Providers/scaledData';
import { DiagramElementProvider } from './Providers/diagramElementContext';
import { Box, BoxProps } from '@chakra-ui/react';
import { ChakraSVG, ChakraSVGProps } from './Generic/styleableSvgComponents';
import { ImageContext, ImageContextProvider } from './Providers/imageContext';

const DEFAULT_RADIUS = 16;

const ScaledImage = memo(function ScaledImageInternal() {
   const zoomContext = useContext(ZoomContext);
   const imageContext = useContext(ImageContext);

   return (
      <g transform={zoomContext.d3Transform.toString()}>
         <image
            width={imageContext.width}
            height={imageContext.height}
            xlinkHref={imageContext.imageUrl}
         />
         <DataPointBackgrounds />
      </g>
   );
});

// Used to hide the text below data points
function DataPointBackgrounds<T extends HotSpotItem>() {
   const clusteredDataContext = useClusteredDataContext<T>();
   const data = clusteredDataContext.data;

   const backgrounds = data.map(({ x, y, radius }, index) => (
      <circle key={index} cx={x} cy={y} r={radius || DEFAULT_RADIUS} fill="white" />
   ));

   return <>{backgrounds}</>;
}

function DataPoints<T extends HotSpotItem>({
   DataPointRenderer,
}: {
   DataPointRenderer: FC<{ cluster: ClusterType<T> }>;
}) {
   const clusteredDataContext = useClusteredDataContext<T>();
   const clusters = clusteredDataContext.clusters;

   const datapoints = clusters.map(cluster => {
      const key = cluster.map(x => x.id).join('-');
      return <DataPointRenderer key={key} cluster={cluster} />;
   });

   return <>{datapoints}</>;
}

type DiagramSVGProps<T extends HotSpotItem> = {
   initialHotSpotId?: string;
   DataPointRenderer: FC<{ cluster: ClusterType<T> }>;
   svgWrapperProps?: BoxProps;
   svgProps?: ChakraSVGProps;
};

export function DiagramSVG<T extends HotSpotItem>({
   initialHotSpotId,
   DataPointRenderer,
   svgWrapperProps,
   svgProps,
}: DiagramSVGProps<T>) {
   const zoomContext = useContext(ZoomContext);
   const clusteredDataContext = useClusteredDataContext<T>();

   const imageContext = useContext(ImageContext);

   const hasImage = imageContext.width > 0 && imageContext.height > 0;
   const zoomInitialized = Boolean(zoomContext.d3Transform);

   useEffect(() => {
      const missingContainer = !zoomContext.containerRef.current;
      if (missingContainer) {
         return;
      }

      const missingEl = !zoomContext.elementRef.current;
      if (missingEl) {
         return;
      }

      if (!hasImage) {
         return;
      }

      if (!initialHotSpotId) {
         zoomContext.initZoom();
         return;
      }

      const initialHotSpotIdExists = clusteredDataContext.data.some(
         ({ id }) => id === initialHotSpotId
      );

      if (!initialHotSpotIdExists) {
         zoomContext.initZoom();
         return;
      }

      // Calulate zoom factor for initial hotspot
      const cluster = clusteredDataContext.getClusterById(initialHotSpotId);
      const zoomFactor =
         cluster.length > 1 ? clusteredDataContext.getFullDeclusterZoomFactor(cluster) : undefined;
      const datapoint = clusteredDataContext.getDatapointById(initialHotSpotId);

      // init at that location
      zoomContext.initZoom(zoomFactor, datapoint.x, datapoint.y);
   }, [
      zoomContext.elementRef?.current,
      zoomContext.containerRef?.current,
      hasImage,
      clusteredDataContext.data.length,
      initialHotSpotId,
      zoomContext.initZoom,
   ]);

   const wrapperRef = useRef<HTMLDivElement>(null);

   const children = (
      <>
         <ScaledImage />
         <DataPoints DataPointRenderer={DataPointRenderer} />
      </>
   );

   return (
      <DiagramElementProvider SVGRef={zoomContext.elementRef} SVGWrapperRef={wrapperRef}>
         <Box {...svgWrapperProps} ref={wrapperRef}>
            <ChakraSVG
               ref={zoomContext.elementRef}
               xmlnsXlink="http://www.w3.org/1999/xlink"
               {...svgProps}
            >
               {zoomInitialized && children}
            </ChakraSVG>
         </Box>
      </DiagramElementProvider>
   );
}

type DiagramProps<T extends HotSpotItem> = {
   initialHotSpotId?: string;
   containerRef?: RefObject<HTMLDivElement | null>;
   defaultScalingComparedToContainer?: number;
   imageSrc: string;
   imageWidth: number;
   imageHeight: number;
} & RequiredDataContextProps<T>;

/**
 * The wrapper that provides context for its children.
 */
function Diagram<T extends HotSpotItem>({
   children,
   imageSrc,
   containerRef,
   defaultScalingComparedToContainer = 0.8,
   data,
   imageWidth,
   imageHeight,
}: PropsWithChildren<DiagramProps<T>>) {
   return (
      <ImageContextProvider imageUrl={imageSrc} width={imageWidth} height={imageHeight}>
         <DataContextProvider data={data}>
            <ZoomableDataProvider
               defaultScalingComparedToContainer={defaultScalingComparedToContainer}
               // @ts-expect-error TS(2322) FIXME: Type 'RefObject<HTMLDivElement> | undefined' is no... Remove this comment to see the full error message
               containerRef={containerRef}
            >
               <ScaledDataProvider>
                  <ClusteredDataProvider>{children}</ClusteredDataProvider>
               </ScaledDataProvider>
            </ZoomableDataProvider>
         </DataContextProvider>
      </ImageContextProvider>
   );
}

export default Diagram;
